import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/8b116605-0899-4056-a35a-3b37f1950c3a/images/a1a204b0185c094cfcefcb312247d07cab928bc9f03429a038a6ceb1a16e876a.png";

const Home = () => {
  const { t } = useLang();
  const [divisions, setDivisions] = useState([]);
  const [featured, setFeatured] = useState([]);

  const VALUES = [
    { n: "01", t: t("value1.title"), d: t("value1.desc") },
    { n: "02", t: t("value2.title"), d: t("value2.desc") },
    { n: "03", t: t("value3.title"), d: t("value3.desc") },
  ];

  useEffect(() => {
    axios.get(`${API}/divisions`).then((r) => setDivisions(r.data)).catch(() => {});
    axios
      .get(`${API}/products?category=sneakers&sort=newest`)
      .then((r) => setFeatured((r.data || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="grain">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#050505]">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover"
            style={{ animation: "fade-up 1.4s ease-out both" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/30 to-[#050505]/80" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(212,175,55,0.18)_0%,transparent_45%)]" />
        </div>

        {/* Animated gold particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-[#D4AF37]"
              style={{
                width: `${1 + (i % 3)}px`,
                height: `${1 + (i % 3)}px`,
                left: `${(i * 5.3 + 7) % 100}%`,
                top: `${(i * 7.7 + 11) % 100}%`,
                opacity: 0.3 + ((i * 0.04) % 0.4),
                boxShadow: "0 0 6px rgba(212, 175, 55, 0.6)",
                animation: `particle-float ${6 + (i % 5)}s ease-in-out ${i * 0.3}s infinite`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 w-full max-w-[1500px] mx-auto px-6 md:px-10 py-32">
          <div className="flex flex-col items-center text-center">
            {/* Central logo */}
            <img
              src="https://customer-assets.emergentagent.com/job_sneaker-hub-242/artifacts/44serdge_Suzel%20gold.png"
              alt="Suzel Holding"
              className="h-48 md:h-72 w-auto mb-10 logo-entrance gold-glow"
              data-testid="hero-logo"
            />

            <div className="flex items-center gap-3 mb-6" style={{ animation: "fade-up 1s 0.8s both" }}>
              <span className="w-2 h-2 bg-[#C5A059] rounded-full pulse-gold" />
              <span className="overline">{t("hero.location")}</span>
            </div>

            <h1
              className="font-display text-[12vw] md:text-[7vw] leading-[0.92] tracking-tighter text-white max-w-5xl"
              style={{ animation: "fade-up 1s 1s both" }}
            >
              {t("hero.title1")}
              <br />
              <span className="italic text-[#C5A059]">{t("hero.title2")}</span>
            </h1>

            <p
              className="mt-8 max-w-xl text-white/80 text-base md:text-lg leading-relaxed"
              style={{ animation: "fade-up 1s 1.2s both", textShadow: "0 2px 20px rgba(0,0,0,0.8)" }}
            >
              {t("hero.subtitle")}
            </p>

            <div
              className="mt-12 flex flex-col sm:flex-row gap-4"
              style={{ animation: "fade-up 1s 1.4s both" }}
            >
              <Link to="/divisions" data-testid="hero-explore-cta" className="btn-gold">
                {t("hero.cta1")} <ArrowRight size={14} strokeWidth={2} />
              </Link>
              <Link to="/sneakers" data-testid="hero-sneakers-cta" className="btn-outline">
                {t("hero.cta2")}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/40">
          <span className="overline text-[10px]">{t("hero.scroll")}</span>
          <ArrowDown size={16} className="animate-bounce" />
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/5 py-6 overflow-hidden bg-[#050505]">
        <div className="flex gap-16 whitespace-nowrap marquee">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-16 items-center">
              {[t("marquee.sneakers"), t("marquee.wigs"), t("marquee.couture"), t("marquee.epicerie"), t("marquee.patisserie")].map((text, j) => (
                <span key={j} className="flex items-center gap-16 text-white/40 font-grotesk text-sm uppercase tracking-[0.32em]">
                  {text}
                  <span className="text-[#C5A059]">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="py-28 md:py-40 px-6 md:px-10">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-24">
            <div>
              <p className="overline mb-4">{t("divisions.overline")}</p>
              <h2 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] text-white max-w-3xl">
                {t("divisions.title1")} <span className="italic text-[#C5A059]">{t("divisions.title2")}</span>
              </h2>
            </div>
            <p className="text-[#A1A1AA] max-w-md leading-relaxed">
              {t("divisions.subtitle")}
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {divisions.map((d, idx) => {
              const layouts = [
                "md:col-span-8 md:row-span-2 aspect-[4/3] md:aspect-auto md:h-[640px]",
                "md:col-span-4 aspect-square md:h-[308px]",
                "md:col-span-4 aspect-square md:h-[308px]",
                "md:col-span-4 aspect-square md:h-[308px]",
                "md:col-span-4 aspect-square md:h-[308px]",
                "md:col-span-4 aspect-square md:h-[308px]",
              ];
              const isHero = idx === 0;
              const linkMap = {
                sneakers: "/sneakers",
                wigs: "/perruques",
                couture: "/haute-couture",
                epicerie: "/epicerie",
                patisserie: "/patisserie",
                printing: "/printing",
              };
              const link = linkMap[d.id] || "/divisions";
              return (
                <Link
                  key={d.id}
                  to={link}
                  data-testid={`division-card-${d.id}`}
                  className={`group relative overflow-hidden border border-white/5 bg-[#0f0f0f] ${layouts[idx]}`}
                >
                  <img
                    src={d.image}
                    alt={d.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent transition-opacity duration-500 group-hover:from-black/70" />
                  <div className="absolute inset-0 p-6 md:p-10 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <span className="overline text-[#C5A059]">{d.accent}</span>
                      <ArrowRight size={18} className="text-white/40 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all duration-500" />
                    </div>
                    <div>
                      <h3 className={`font-display ${isHero ? "text-5xl md:text-7xl" : "text-3xl md:text-4xl"} tracking-tighter text-white leading-[0.95] mb-2`}>
                        {d.name}
                      </h3>
                      <p className="text-[#C5A059] text-sm font-grotesk mb-3">{d.tagline}</p>
                      {isHero && (
                        <p className="text-white/70 text-sm max-w-md leading-relaxed">{d.description}</p>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED SNEAKERS */}
      {featured.length > 0 && (
        <section className="py-28 md:py-40 px-6 md:px-10 bg-[#080808]">
          <div className="max-w-[1500px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div>
                <p className="overline mb-4">{t("featured.overline")}</p>
                <h2 className="font-display text-4xl md:text-6xl tracking-tighter text-white">
                  {t("featured.title1")} <span className="italic text-[#C5A059]">{t("featured.title2")}</span>
                </h2>
              </div>
              <Link to="/sneakers" data-testid="see-all-sneakers-link" className="link-underline overline">
                {t("featured.seeAll")}
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featured.map((p) => (
                <Link
                  to={`/sneakers/${p.slug}`}
                  key={p.id}
                  data-testid={`featured-product-${p.slug}`}
                  className="group block bg-[#0f0f0f] border border-white/5 hover:border-[#C5A059]/30 transition-colors duration-500"
                >
                  <div className="aspect-[4/3] bg-[#050505] overflow-hidden flex items-center justify-center p-8">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1000ms]" />
                  </div>
                  <div className="p-6 flex items-start justify-between gap-4">
                    <div>
                      <p className="overline text-[10px] mb-2">{p.brand}</p>
                      <h3 className="font-display text-xl text-white leading-tight">{p.name}</h3>
                    </div>
                    <span className="font-grotesk text-[#C5A059] shrink-0">{p.price.toLocaleString("fr-FR")} $</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VALUES */}
      <section className="py-28 md:py-40 px-6 md:px-10">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="overline mb-4">{t("values.overline")}</p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter text-white leading-[1] mb-6">
              {t("values.title1")} <span className="italic text-[#C5A059]">{t("values.title2")}</span>
            </h2>
            <p className="text-[#A1A1AA] leading-relaxed max-w-md mb-8">
              {t("values.subtitle")}
            </p>
            <Link to="/a-propos" data-testid="values-learn-more" className="btn-outline">
              {t("values.cta")}
            </Link>
          </div>
          <div className="md:col-span-7 space-y-6">
            {VALUES.map((v) => (
              <div key={v.n} data-testid={`value-${v.t.toLowerCase()}`} className="border-t border-white/10 pt-6 flex gap-8">
                <span className="font-display text-3xl text-[#C5A059] shrink-0">{v.n}</span>
                <div>
                  <h3 className="font-display text-2xl md:text-3xl text-white mb-2">{v.t}</h3>
                  <p className="text-[#A1A1AA] leading-relaxed max-w-xl">{v.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 pb-28">
        <div className="max-w-[1500px] mx-auto bg-[#0e0e0e] border border-[#C5A059]/20 p-10 md:p-20 text-center">
          <p className="overline mb-6">{t("homeCta.overline")}</p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter text-white mb-6">
            {t("homeCta.title1")} <span className="italic text-[#C5A059]">{t("homeCta.title2")}</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-xl mx-auto mb-10 leading-relaxed">
            {t("homeCta.subtitle")}
          </p>
          <Link to="/contact" data-testid="home-cta-contact" className="btn-gold">
            {t("homeCta.button")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
